import { useRouter } from "next/router";
import { FC, useContext, useMemo } from "react";
import { FeedContext } from "@finbackoffice/site-core";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import SportsLayout from "components/layout/sportsbook/SportsLayout";
import { useSocketStorage, FeedTypeEnum } from "hooks";
import Loading from "components/base/loading/Loading";
import LiveCategories from "./layouts/categories/LiveCategories";
import LiveTopLeagues from "./layouts/top-leagues/LiveTopLeagues";

type IProps = {
    layout: "tournaments" | "categories";
};

const LiveSports: FC<IProps> = ({ layout }) => {
    const router = useRouter();
    const { getCategories, getSportBySlug, connected } = useSocketStorage(FeedTypeEnum.live);
    const { liveFeed, liveIsLoading, hasLiveFeedGames } = useContext(FeedContext);
    const ready = connected && !liveIsLoading && hasLiveFeedGames;

    const activeSportId = useMemo(
        () => (ready ? (getSportBySlug(router.query.sport as string)?.id ?? liveFeed[0].id) : null),
        [getSportBySlug, ready, router.query.sport, liveFeed],
    );

    const renderLayout = useMemo(() => {
        let loading;
        let content;
        switch (layout) {
            case "categories":
                loading = !(ready && activeSportId);
                content = activeSportId ? (
                    <ErrorBoundary name={SportsLayout.name}>
                        <SportsLayout sports={liveFeed} activeSportId={activeSportId}>
                            <LiveCategories
                                key={activeSportId}
                                sportId={activeSportId}
                                categories={getCategories(activeSportId)}
                            />
                        </SportsLayout>
                    </ErrorBoundary>
                ) : null;
                break;
            case "tournaments":
                loading = !ready;
                content = (
                    <ErrorBoundary name={SportsLayout.name}>
                        <SportsLayout sports={liveFeed}>
                            <ErrorBoundary name={LiveTopLeagues.name}>
                                <LiveTopLeagues />
                            </ErrorBoundary>
                        </SportsLayout>
                    </ErrorBoundary>
                );
                break;
            default:
                content = null;
        }

        return loading ? <Loading /> : content;
    }, [activeSportId, getCategories, layout, liveFeed, ready]);

    return renderLayout;
};

export default LiveSports;
